import { IntlShape } from 'react-intl';
import { CreditCycle } from 'store/price/types';

const CYCLE_DAY = /^\d{4}-\d{2}-\d{2}$/;
// The cycle days are calendar days in the building's time zone, so they're read and formatted in UTC to stay the
// same day wherever the device is.
const CYCLE_DAY_FORMAT = { month: 'short', day: 'numeric', timeZone: 'UTC' } as const;
// The last assigned day is usually at the end of a year, so it keeps the year.
const ASSIGNED_DAY_FORMAT = { ...CYCLE_DAY_FORMAT, year: 'numeric' } as const;

const parseCycleDay = (day?: string): Date | null => {
  if (!day || !CYCLE_DAY.test(day)) {
    return null;
  }
  const date = new Date(`${day}T00:00:00Z`);

  // An impossible day such as 2026-02-30 rolls over into the next month, so it must read back unchanged.
  return Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== day ? null : date;
};

// The cycle's first and last day as short localized dates (`Nov 13`, `13 nov.`), or null when they can't be read.
export const formatCreditCycleDays = (cycle: CreditCycle, intl: IntlShape): { start: string; end: string } | null => {
  const start = parseCycleDay(cycle?.start_date);
  const end = parseCycleDay(cycle?.end_date);
  if (!start || !end) {
    return null;
  }

  return { start: intl.formatDate(start, CYCLE_DAY_FORMAT), end: intl.formatDate(end, CYCLE_DAY_FORMAT) };
};

// Checkout subtitle of the credits option when no credit covers the booking date but the user's credits end before it:
// "No credits assigned past Dec 31, 2026" rather than "Not enough credits", which reads as if they had none.
export const getCreditsAssignedUntilSubtitle = (
  creditsAssignedUntil: string,
  availableCredit: number,
  intl: IntlShape,
): string => {
  const day = parseCycleDay(creditsAssignedUntil);

  return day
    ? intl.formatMessage({ id: 'no_credits_assigned_past' }, { date: intl.formatDate(day, ASSIGNED_DAY_FORMAT) })
    : intl.formatMessage({ id: 'not_enough_credits_subtitle' }, { credits: availableCredit });
};

// Checkout subtitle of the credits option when the booking is paid from a cycle of a recurring allowance, current or
// later. `availableCredit` is that cycle's balance.
export const getCreditCycleSubtitle = (
  cycle: CreditCycle,
  hasEnoughCredits: boolean,
  availableCredit: number,
  intl: IntlShape,
): string => {
  const days = formatCreditCycleDays(cycle, intl);

  if (!days) {
    return hasEnoughCredits
      ? intl.formatMessage({ id: 'credits_from_booking_cycle_fallback' })
      : intl.formatMessage({ id: 'not_enough_credits_subtitle' }, { credits: availableCredit });
  }

  return intl.formatMessage(
    { id: hasEnoughCredits ? 'credits_from_booking_cycle' : 'not_enough_credits_in_booking_cycle' },
    days,
  );
};
